export type ToolId =
  | "cut"
  | "frame"
  | "text"
  | "sound"
  | "look"
  // "motion" is NO LONGER a dock TAB — camera moves fold into the Look tool as a
  // beat-scoped timed effect (see LookTool's "Camera move" group). The id survives
  // ONLY as a store-routing key so the Look tool's camera-move cart can open the
  // "Extra camera moves" pack (store category `motion`). It is intentionally
  // absent from CORE_DOCK_ORDER / TOOLS, so no Motion tab renders.
  | "motion"
  | "decorate"
  | "cast";

/** Player-facing dock labels (internal ids stay cut/frame/text/sound/…). */
export const TOOL_LABELS: Record<ToolId, string> = {
  cut: "Clips",
  frame: "Focus",
  text: "Caption",
  sound: "Sound",
  look: "Look",
  motion: "Motion",
  decorate: "Sticker",
  cast: "Cast",
};

/** Mockup dock glyphs (matches …/targets/nav-2-Editor.png). */
export const TOOL_ICONS: Record<ToolId, string> = {
  cut: "✂",
  frame: "◎",
  text: "T",
  sound: "♪",
  look: "◐",
  motion: "↗",
  decorate: "✦",
  cast: "⇄",
};

export function dockLabelFor(toolId: ToolId, packId: string): string {
  void packId;
  return TOOL_LABELS[toolId];
}

export const TOOLS: Array<{ id: ToolId; label: string }> = [
  { id: "cut", label: TOOL_LABELS.cut },
  { id: "frame", label: TOOL_LABELS.frame },
  { id: "text", label: TOOL_LABELS.text },
  { id: "sound", label: TOOL_LABELS.sound },
  { id: "look", label: TOOL_LABELS.look },
  // Motion is folded into Look (camera moves), so it is not a standalone tool.
  { id: "decorate", label: TOOL_LABELS.decorate },
  { id: "cast", label: TOOL_LABELS.cast },
];

/** Hide Cast entirely when the active pack has no castSlots. */
export function toolsForPack(hasCastSlots: boolean): typeof TOOLS {
  if (hasCastSlots) return TOOLS;
  return TOOLS.filter((t) => t.id !== "cast");
}

export type DockTool = {
  id: ToolId;
  label: string;
  locked?: boolean;
  lockReason?: string;
};

const COACH: Record<ToolId, string> = {
  cut: "Tap a beat card to keep or hide it.",
  frame: "Zoom in to crop, then drag the video to pan.",
  text: "Pick how the headline spins the footage.",
  sound: "Pick music or a sound effect.",
  look: "Tap a look for the whole reel.",
  motion: "Pick a beat, then a camera move.",
  decorate: "Add a sticker.",
  cast: "Pick how the cat looks.",
};

export function coachLineFor(toolId: ToolId): string {
  return COACH[toolId];
}

/** Fixed dock order — the core icon tools shown in every story. */
const CORE_DOCK_ORDER: ToolId[] = ["cut", "frame", "text", "sound", "look"];

/**
 * Flat dock: the core icon tools (Clips / Focus / Caption / Sound / Look) in a
 * single scrolling row, then Decorate. Two tabs used to live here and were
 * removed from the dock: Motion (camera moves folded into the Look tool) and
 * Cast (the cat-expression variants). The cast subsystem stays in code but is no
 * longer surfaced as a tab, so `hasCastSlots` / `castingUnlocked` remain on the
 * signature for callers without adding a tab.
 */
export function dockToolsForPack(args: {
  packId: string;
  hasCastSlots: boolean;
  castingUnlocked: boolean;
}): DockTool[] {
  const { packId } = args;
  const label = (id: ToolId) => dockLabelFor(id, packId);
  const tools: DockTool[] = CORE_DOCK_ORDER.map((id) => ({ id, label: label(id) }));
  tools.push({ id: "decorate", label: label("decorate") });
  return tools;
}

/** Player-facing cast names — pack export metadata keeps authored labels. */
export function playerFacingCastLabel(variant: {
  id: string;
  label: string;
  sourceFaithful?: boolean;
}): string {
  if (variant.sourceFaithful || /source/i.test(variant.id)) return "Original";
  if (/vulnerable|worried/i.test(variant.id) || /vulnerable/i.test(variant.label)) {
    return "Worried-looking";
  }
  if (/accusatory|dramatic/i.test(variant.id) || /accusatory/i.test(variant.label)) {
    return "Dramatic-looking";
  }
  return variant.label.replace(/\s*\([^)]*\)\s*/g, " ").replace(/\s+/g, " ").trim();
}
